import {
  Component,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Input
} from '@angular/core';
import { PostType } from '@eview/core/domain/post/post';
import { AppState } from '@eview/core/store/states/app.state';
import { Store } from '@ngrx/store';
import { Form, sortFormsDefaultFirst } from '@eview/core/domain/form/form';
import { selectForms } from '@eview/core/store/selectors/forms.selector';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { combineLatest, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { AuthHelpers } from '@eview/core/auth';
import { Permission } from '@eview/core/auth/permission';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';
@Component({
  selector: 'eview-forms',
  templateUrl: 'forms.component.html',
  styleUrls: ['forms.component.scss']
})
export class FormsComponent implements OnInit, OnDestroy, OnChanges {

  private subs: Subscription;
  public userCanAccessImportExport: boolean;
  formList: Form[] = [];
  selectedFormId: number;
  @Input() formId: number;
  @Input() type: string;
  @Input() showDropdown: Boolean;
  @Input() showAllMapFormats = false;
  @Input() restrictAnonymousMapFormatsToIds: number[] | null = null;
  @Output() onFormSelect: EventEmitter<Number> = new EventEmitter();
  currentLang: string;
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private translate: TranslateService
  ) {
    this.subs = new Subscription();
    this.currentLang =
      this.translate.currentLang || this.translate.getDefaultLang() || 'es';
  }

  ngOnInit() {
    this.selectedFormId = this.formId ? this.formId : null;

    this.subs.add(
      combineLatest([
        this.store.select(selectForms),
        AuthHelpers.User.AccessibleFormIds(this.store, 'create'),
        this.store.select(selectUser)
      ]).subscribe(([forms, createFormIds, user]) => {
          const withLabels = forms.results.map(form => ({
            ...form,
            form_name:
              this.currentLang === 'am' ? form['name_am'] : form.name
          }));

          if (this.showAllMapFormats) {
            let formLists = withLabels.filter(
              form => !form.disabled && this.isMappableReportFormType(form)
            );
            if (
              user == null &&
              this.restrictAnonymousMapFormatsToIds !== null
            ) {
              const allow = new Set(this.restrictAnonymousMapFormatsToIds);
              formLists = formLists.filter(f => allow.has(f.id));
            }
            this.formList = sortFormsDefaultFirst(formLists);
            this.ensureSelectedFormInList();
            return;
          }

          const allCreate = createFormIds === null;
          const allowed = new Set(createFormIds || []);

          const formLists = withLabels.filter(form => {
            if (form.disabled) {
              return false;
            }
            return allCreate || allowed.has(form.id);
          });

          this.formList = sortFormsDefaultFirst(formLists);
          this.ensureSelectedFormInList();
        })
    );

    // Handle language change and update the form names accordingly
    this.subs.add(
      this.translate.onLangChange.subscribe((langChangeEvent: LangChangeEvent) => {
        this.currentLang = langChangeEvent.lang;
        this.formList = this.formList.map(form => {
          return {
            ...form,
            form_name: langChangeEvent.lang === 'am' ? form['name_am'] : form.name
          };
        });
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formId && changes.formId.currentValue != null) {
      this.selectedFormId = Number(changes.formId.currentValue);
      this.ensureSelectedFormInList();
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /** Keeps ngModel aligned with forms returned by the API (avoids stale ids such as 5). */
  private ensureSelectedFormInList(): void {
    if (!this.formList?.length) {
      return;
    }
    const ids = new Set(this.formList.map(f => Number(f.id)));
    const current = Number(this.selectedFormId);
    if (!ids.has(current)) {
      this.selectedFormId = this.formList[0].id;
      this.formId = this.selectedFormId;
      this.onFormSelect.emit(this.selectedFormId);
    }
  }

  get formSelectOptions(): SearchableSelectOption[] {
    return (this.formList || []).map(form => ({
      value: form.id,
      label: (form as { form_name?: string }).form_name || form.name
    }));
  }

  onFormChange() {
    if (this.selectedFormId == null) {
      return;
    }
    this.formId = this.selectedFormId;
    this.onFormSelect.emit(this.formId);
  }

  private isMappableReportFormType(form: Form): boolean {
    const t = form.type;
    if (t == null || String(t).trim() === '') {
      return true;
    }
    const s = String(t);
    return (
      s === PostType.Incident ||
      s === PostType.Risk ||
      s === 'incident' ||
      s === 'risk' ||
      s === 'report'
    );
  }

}
